/**
 * CLI UI utilities for rich terminal output
 * Provides colors, spinners, progress bars, and formatted boxes
 */

import chalk from 'chalk';
import ora, { type Ora } from 'ora';
import cliProgress from 'cli-progress';
import figures from 'figures';

// ============================================================================
// Environment Detection
// ============================================================================

/**
 * Check if the terminal supports interactive features (spinners, colors)
 */
function isInteractive(): boolean {
  // Disable in CI environments
  if (process.env.CI || process.env.GITHUB_ACTIONS || process.env.GITLAB_CI) {
    return false;
  }
  // Check if stdout is a TTY
  return Boolean(process.stdout.isTTY);
}

/**
 * Check if colors should be enabled
 */
function supportsColor(): boolean {
  // Respect NO_COLOR environment variable
  if (process.env.NO_COLOR) {
    return false;
  }
  // Force color if requested
  if (process.env.FORCE_COLOR) {
    return true;
  }
  return Boolean(process.stdout.isTTY);
}

// ============================================================================
// Colors
// ============================================================================

/**
 * Color utilities - automatically handles non-color environments
 */
export const colors = {
  // Status colors
  success: (text: string) => (supportsColor() ? chalk.green(text) : text),
  error: (text: string) => (supportsColor() ? chalk.red(text) : text),
  warning: (text: string) => (supportsColor() ? chalk.yellow(text) : text),
  info: (text: string) => (supportsColor() ? chalk.cyan(text) : text),

  // Text styles
  bold: (text: string) => (supportsColor() ? chalk.bold(text) : text),
  dim: (text: string) => (supportsColor() ? chalk.dim(text) : text),
  italic: (text: string) => (supportsColor() ? chalk.italic(text) : text),
  underline: (text: string) => (supportsColor() ? chalk.underline(text) : text),

  // Specific colors
  white: (text: string) => (supportsColor() ? chalk.white(text) : text),
  gray: (text: string) => (supportsColor() ? chalk.gray(text) : text),
  blue: (text: string) => (supportsColor() ? chalk.blue(text) : text),
  magenta: (text: string) => (supportsColor() ? chalk.magenta(text) : text),

  // Combined styles
  successBold: (text: string) =>
    supportsColor() ? chalk.green.bold(text) : text,
  errorBold: (text: string) => (supportsColor() ? chalk.red.bold(text) : text),
  url: (text: string) => (supportsColor() ? chalk.blue.underline(text) : text),
  label: (text: string) => (supportsColor() ? chalk.white.bold(text) : text),
  value: (text: string) => (supportsColor() ? chalk.white(text) : text),
};

// ============================================================================
// Symbols
// ============================================================================

/**
 * Cross-platform symbols
 */
export const symbols = {
  success: figures.tick,
  error: figures.cross,
  warning: figures.warning,
  info: figures.info,
  bullet: figures.bullet,
  pointer: figures.pointer,
  arrowRight: figures.arrowRight,
  line: figures.line,
  ellipsis: figures.ellipsis,
};

// ============================================================================
// Box Drawing
// ============================================================================

interface BoxOptions {
  padding?: number;
  borderColor?: 'green' | 'red' | 'yellow' | 'cyan' | 'white';
  title?: string;
}

/**
 * Create a bordered box around content
 */
export function box(content: string[], options: BoxOptions = {}): string {
  const { padding = 1, borderColor = 'white', title } = options;

  // Calculate the maximum width
  const maxContentWidth = Math.max(
    ...content.map(line => stripAnsi(line).length),
    title ? stripAnsi(title).length : 0
  );
  const boxWidth = maxContentWidth + padding * 2 + 2;

  // Border characters
  const chars = {
    topLeft: '┌',
    topRight: '┐',
    bottomLeft: '└',
    bottomRight: '┘',
    horizontal: '─',
    vertical: '│',
  };

  // Color function based on borderColor
  const colorFn =
    borderColor === 'green'
      ? colors.success
      : borderColor === 'red'
        ? colors.error
        : borderColor === 'yellow'
          ? colors.warning
          : borderColor === 'cyan'
            ? colors.info
            : colors.white;

  // Build the box
  const lines: string[] = [];

  // Top border with optional title
  if (title) {
    const titleText = ` ${title} `;
    const remainingWidth = boxWidth - 2 - titleText.length;
    const leftPadding = Math.floor(remainingWidth / 2);
    const rightPadding = remainingWidth - leftPadding;
    lines.push(
      colorFn(chars.topLeft) +
        colorFn(chars.horizontal.repeat(leftPadding)) +
        colors.bold(titleText) +
        colorFn(chars.horizontal.repeat(rightPadding)) +
        colorFn(chars.topRight)
    );
  } else {
    lines.push(
      colorFn(chars.topLeft) +
        colorFn(chars.horizontal.repeat(boxWidth - 2)) +
        colorFn(chars.topRight)
    );
  }

  // Empty padding line
  if (padding > 0) {
    lines.push(
      colorFn(chars.vertical) +
        ' '.repeat(boxWidth - 2) +
        colorFn(chars.vertical)
    );
  }

  // Content lines
  for (const line of content) {
    const lineLength = stripAnsi(line).length;
    const rightPad = boxWidth - 2 - padding - lineLength - padding;
    lines.push(
      colorFn(chars.vertical) +
        ' '.repeat(padding) +
        line +
        ' '.repeat(Math.max(0, rightPad + padding)) +
        colorFn(chars.vertical)
    );
  }

  // Empty padding line
  if (padding > 0) {
    lines.push(
      colorFn(chars.vertical) +
        ' '.repeat(boxWidth - 2) +
        colorFn(chars.vertical)
    );
  }

  // Bottom border
  lines.push(
    colorFn(chars.bottomLeft) +
      colorFn(chars.horizontal.repeat(boxWidth - 2)) +
      colorFn(chars.bottomRight)
  );

  return lines.join('\n');
}

/**
 * Strip ANSI escape codes from a string
 */
function stripAnsi(str: string): string {
  // eslint-disable-next-line no-control-regex
  return str.replace(/\x1B\[[0-9;]*[a-zA-Z]/g, '');
}

// ============================================================================
// Spinners
// ============================================================================

/**
 * Spinner wrapper that handles non-interactive environments
 */
export interface Spinner {
  start(text?: string): void;
  succeed(text?: string): void;
  fail(text?: string): void;
  warn(text?: string): void;
  info(text?: string): void;
  stop(): void;
  text: string;
}

class InteractiveSpinner implements Spinner {
  private spinner: Ora;

  constructor(text: string) {
    this.spinner = ora({
      text,
      spinner: 'dots',
      color: 'cyan',
    });
  }

  get text(): string {
    return this.spinner.text;
  }

  set text(value: string) {
    this.spinner.text = value;
  }

  start(text?: string): void {
    this.spinner.start(text);
  }

  succeed(text?: string): void {
    this.spinner.succeed(text);
  }

  fail(text?: string): void {
    this.spinner.fail(text);
  }

  warn(text?: string): void {
    this.spinner.warn(text);
  }

  info(text?: string): void {
    this.spinner.info(text);
  }

  stop(): void {
    this.spinner.stop();
  }
}

class StaticSpinner implements Spinner {
  private _text: string;

  constructor(text: string) {
    this._text = text;
  }

  get text(): string {
    return this._text;
  }

  set text(value: string) {
    this._text = value;
  }

  start(text?: string): void {
    console.log(`  ${symbols.pointer} ${text || this._text}`);
  }

  succeed(text?: string): void {
    console.log(`  ${colors.success(symbols.success)} ${text || this._text}`);
  }

  fail(text?: string): void {
    console.log(`  ${colors.error(symbols.error)} ${text || this._text}`);
  }

  warn(text?: string): void {
    console.log(`  ${colors.warning(symbols.warning)} ${text || this._text}`);
  }

  info(text?: string): void {
    console.log(`  ${colors.info(symbols.info)} ${text || this._text}`);
  }

  stop(): void {
    // No-op for static spinner
  }
}

/**
 * Create a spinner (interactive or static based on environment)
 */
export function createSpinner(text: string): Spinner {
  if (isInteractive()) {
    return new InteractiveSpinner(text);
  }
  return new StaticSpinner(text);
}

// ============================================================================
// Progress Bar
// ============================================================================

export interface ProgressBar {
  start(total: number, startValue?: number): void;
  update(value: number, payload?: Record<string, string>): void;
  increment(payload?: Record<string, string>): void;
  stop(): void;
}

class InteractiveProgressBar implements ProgressBar {
  private bar: cliProgress.SingleBar;

  constructor() {
    this.bar = new cliProgress.SingleBar(
      {
        format: '  {bar} {percentage}% │ {value}/{total} │ {filename}',
        barCompleteChar: '█',
        barIncompleteChar: '░',
        hideCursor: true,
        clearOnComplete: true,
        barsize: 30,
      },
      cliProgress.Presets.shades_classic
    );
  }

  start(total: number, startValue: number = 0): void {
    this.bar.start(total, startValue, { filename: '' });
  }

  update(value: number, payload?: Record<string, string>): void {
    this.bar.update(value, payload);
  }

  increment(payload?: Record<string, string>): void {
    if (payload) {
      this.bar.increment(payload);
    } else {
      this.bar.increment();
    }
  }

  stop(): void {
    this.bar.stop();
  }
}

class StaticProgressBar implements ProgressBar {
  private total: number = 0;
  private current: number = 0;

  start(total: number, startValue: number = 0): void {
    this.total = total;
    this.current = startValue;
  }

  update(value: number, payload?: Record<string, string>): void {
    this.current = value;
    const filename = payload?.filename || '';
    console.log(
      `  ${colors.success(symbols.success)} ${filename} (${value}/${this.total})`
    );
  }

  increment(payload?: Record<string, string>): void {
    this.current++;
    const filename = payload?.filename || '';
    console.log(`  ${colors.success(symbols.success)} ${filename}`);
  }

  stop(): void {
    // No-op
  }
}

/**
 * Create a progress bar (interactive or static based on environment)
 */
export function createProgressBar(): ProgressBar {
  if (isInteractive()) {
    return new InteractiveProgressBar();
  }
  return new StaticProgressBar();
}

// ============================================================================
// Formatting Utilities
// ============================================================================

/**
 * Format a key-value pair with consistent spacing
 */
export function formatKeyValue(
  key: string,
  value: string | number | boolean,
  keyWidth: number = 16
): string {
  const paddedKey = key.padEnd(keyWidth);
  return `  ${colors.label(paddedKey)} ${colors.value(String(value))}`;
}

/**
 * Format a section header
 */
export function formatSection(title: string): string {
  return `\n${colors.bold(title)}`;
}

/**
 * Format a URL for display
 */
export function formatUrl(url: string): string {
  return colors.url(url);
}

// ============================================================================
// Preset Messages
// ============================================================================

/**
 * Print a header box for the CLI
 */
export function printHeader(
  version: string,
  action: string = 'Uploading Playwright reports'
): void {
  console.log(
    box([`TestDino CLI v${version}`, colors.dim(action)], {
      borderColor: 'cyan',
    })
  );
  console.log();
}

/**
 * Print a success box
 */
export function printSuccess(message: string, url?: string): void {
  const content = [colors.success(`${symbols.success} ${message}`)];
  if (url) {
    content.push('');
    content.push('View results:');
    content.push(formatUrl(url));
  }
  console.log();
  console.log(box(content, { borderColor: 'green' }));
}

/**
 * Print an error box
 */
export function printError(
  title: string,
  message: string,
  suggestions?: string[]
): void {
  const content = [colors.error(`${symbols.error} ${title}`)];
  content.push('');
  content.push(message);

  if (suggestions && suggestions.length > 0) {
    content.push('');
    content.push(colors.dim('Troubleshooting:'));
    for (const suggestion of suggestions) {
      content.push(`${colors.dim(symbols.bullet)} ${suggestion}`);
    }
  }

  console.log();
  console.log(box(content, { borderColor: 'red' }));
}

// ============================================================================
// Help Formatting
// ============================================================================

/**
 * Print help header with version
 */
export function printHelpHeader(version: string, description: string): void {
  console.log(
    box([`TestDino CLI v${version}`, colors.dim(description)], {
      borderColor: 'cyan',
      padding: 1,
    })
  );
}

/**
 * Print a section title for help
 */
export function printHelpSection(title: string): void {
  console.log(`\n${colors.bold(title)}`);
}

/**
 * Print a command entry for help
 */
export function printHelpCommand(
  name: string,
  description: string,
  nameWidth: number = 20
): void {
  console.log(
    `  ${colors.info(name.padEnd(nameWidth))} ${colors.dim(description)}`
  );
}

/**
 * Print an option entry for help
 */
export function printHelpOption(
  flags: string,
  description: string,
  flagWidth: number = 28
): void {
  console.log(`  ${colors.gray(flags.padEnd(flagWidth))} ${description}`);
}

/**
 * Print an example for help
 */
export function printHelpExample(command: string, description?: string): void {
  if (description) {
    console.log(`  ${colors.dim(description)}`);
  }
  console.log(`  ${colors.white('$')} ${command}`);
}

/**
 * Print a key-value pair for help (env vars, etc)
 */
export function printHelpEnvVar(
  name: string,
  description: string,
  nameWidth: number = 22
): void {
  console.log(
    `  ${colors.gray(name.padEnd(nameWidth))} ${colors.dim(description)}`
  );
}
